import BookOnlineIcon from '@mui/icons-material/BookOnline'
import BusinessIcon from '@mui/icons-material/Business'
import EmailIcon from '@mui/icons-material/Email'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React from 'react'
import { REGISTER_ROUTE } from '../../../config'
import { GridItemComponent } from '../styled'

const CONTACT_ITEMS = [
  {
    href: 'tel:+19793099335',
    icon: <LocalPhoneIcon fontSize="small" />,
    label: 'Call Us',
    value: '979-309-9335',
    detail: 'Talk with the local team about availability and move-in timing.',
  },
  {
    href: 'https://goo.gl/maps/yEZN95uVfmM2',
    icon: <BusinessIcon fontSize="small" />,
    label: 'Visit Us',
    value: '2247 South US 77, Schulenburg, TX 78956',
    detail: 'Convenient access right off the highway for quick stops.',
  },
  {
    href: 'mailto:general@justlockit.com',
    icon: <EmailIcon fontSize="small" />,
    label: 'Email',
    value: 'general@justlockit.com',
    detail:
      'Reach out anytime for reservations, billing, or general questions.',
  },
]

const SUPPORT_POINTS = [
  'Month-to-month leasing',
  'Online reservations',
  'Fast local support',
]

export const HomeBottomRowLeftComponent: React.FC<{
  onChangeRoute: (path: string) => void
}> = ({ onChangeRoute }) => (
  <GridItemComponent
    sx={{
      alignItems: 'stretch',
      justifyContent: 'space-between',
      overflow: 'hidden',
      border: '1px solid rgba(173, 223, 255, 0.08)',
      background:
        'radial-gradient(circle at top left, rgba(78, 164, 208, 0.18) 0%, rgba(78, 164, 208, 0) 32%), linear-gradient(180deg, rgba(14, 18, 24, 0.96) 0%, rgba(11, 15, 20, 1) 100%)',
      boxShadow: '0 18px 36px rgba(0, 0, 0, 0.2)',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flex: 1,
        p: { xs: 2.25, sm: 3 },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
        <Typography
          sx={{
            color: 'rgba(180, 226, 255, 0.94)',
            fontSize: '0.74rem',
            fontWeight: 800,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
        >
          Contact
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: '#f6fbff',
            fontSize: { xs: '1.5rem', sm: '1.8rem' },
            fontWeight: 700,
            lineHeight: 1.08,
            maxWidth: 420,
          }}
        >
          Need help choosing a size or getting moved in quickly?
        </Typography>
        <Typography
          sx={{
            color: 'rgba(230, 240, 250, 0.8)',
            fontSize: '0.98rem',
            lineHeight: 1.6,
            maxWidth: 440,
          }}
        >
          Reach our Schulenburg location for storage availability, parking,
          access, and reservation questions. The page feels polished, but the
          support stays local and straightforward.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.9,
          mt: 2.2,
        }}
      >
        {SUPPORT_POINTS.map((point) => (
          <Box
            key={point}
            sx={{
              px: 1.15,
              py: 0.7,
              border: '1px solid rgba(173, 223, 255, 0.12)',
              backgroundColor: 'rgba(15, 22, 31, 0.56)',
              color: '#d8f3ff',
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            {point}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mt: 2.2 }}>
        {CONTACT_ITEMS.map(({ href, icon, label, value, detail }) => (
          <Box
            key={label}
            component="a"
            href={href}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              px: 1.4,
              py: 1.3,
              textDecoration: 'none',
              color: 'inherit',
              border: '1px solid rgba(173, 223, 255, 0.12)',
              backgroundColor: 'rgba(15, 22, 31, 0.62)',
              transition: 'border-color 160ms ease, transform 160ms ease',
              '&:hover': {
                borderColor: 'rgba(120, 216, 255, 0.34)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                color: '#9edaff',
                backgroundColor: 'rgba(37, 74, 103, 0.34)',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
              <Typography
                sx={{
                  color: 'rgba(180, 226, 255, 0.82)',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  color: '#f6fbff',
                  fontSize: '1rem',
                  fontWeight: 600,
                  lineHeight: 1.35,
                }}
              >
                {value}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(230, 240, 250, 0.72)',
                  fontSize: '0.9rem',
                  lineHeight: 1.45,
                }}
              >
                {detail}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>

    <Box sx={{ p: { xs: 2.25, sm: 3 }, pt: 0, width: '100%' }}>
      <Box
        sx={{
          p: 1.4,
          border: '1px solid rgba(173, 223, 255, 0.12)',
          background:
            'linear-gradient(180deg, rgba(11, 18, 27, 0.7) 0%, rgba(9, 15, 22, 0.96) 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1.4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              color: '#9edaff',
              backgroundColor: 'rgba(37, 74, 103, 0.34)',
              flexShrink: 0,
            }}
          >
            <ScheduleOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography
              sx={{
                color: 'rgba(180, 226, 255, 0.82)',
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
            >
              Ready When You Are
            </Typography>
            <Typography
              sx={{
                color: '#f6fbff',
                fontSize: '0.98rem',
                fontWeight: 600,
              }}
            >
              Reserve online now and lock in your space.
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={() => onChangeRoute(REGISTER_ROUTE)}
          variant="contained"
          color="info"
          startIcon={<BookOnlineIcon />}
          fullWidth
          sx={{
            px: 2.5,
            whiteSpace: 'nowrap',
            mt: 'auto',
          }}
        >
          Reserve Units
        </Button>
      </Box>
    </Box>
  </GridItemComponent>
)
